import React, { useEffect, useRef } from 'react';
import { loadExternalScript } from '../../utils/scriptLoader';

const D3_URL = 'https://d3js.org/d3.v7.min.js';

function CalendarHeatmap({ entries, year }) {
  const chartRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      if (!chartRef.current) return;
      const container = chartRef.current;
      container.innerHTML = '';

      if (!entries || entries.length === 0 || !year) {
        return;
      }

      const d3 = await loadExternalScript(D3_URL, 'd3');
      if (cancelled) return;

      const cellSize = 25;
      const monthsPerRow = 3;
      const margin = { top: 30, right: 30, bottom: 30, left: 30 };

      const colorMap = {
        winning: '#96e6ab',
        losing: '#df87a8',
        breakeven: '#76ace0',
        pending: '#ddd',
      };

      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31);
      const allDays = d3.timeDays(startDate, d3.timeDay.offset(endDate, 1));

      const dayStatusMap = {};
      entries.forEach((entry) => {
        const key = d3.timeFormat('%Y-%m-%d')(entry.date);
        if (entry.change > 0) dayStatusMap[key] = 'winning';
        else if (entry.change < 0) dayStatusMap[key] = 'losing';
        else dayStatusMap[key] = 'breakeven';
      });

      const monthsData = d3.rollup(allDays, (days) => days, (date) => date.getMonth());

      const monthWidth = 7 * cellSize;
      const monthHeight = 6 * cellSize;
      const totalRows = Math.ceil(12 / monthsPerRow);

      const svgWidth =
        margin.left +
        margin.right +
        monthsPerRow * monthWidth +
        (monthsPerRow - 1) * 50;
      const svgHeight =
        margin.top +
        margin.bottom +
        totalRows * monthHeight +
        (totalRows - 1) * 50;

      const targetWidth = (container && container.clientWidth) ? container.clientWidth : svgWidth;
      const scale = targetWidth / svgWidth;

      const svg = d3
        .select(container)
        .append('svg')
        .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('width', targetWidth)
        .attr('height', svgHeight * scale)
        .style('width', '100%')
        .style('height', 'auto')
        .style('background', 'white');

      const root = svg.append('g').attr('transform', `scale(${scale})`);

      Array.from(monthsData.entries()).forEach(([, days], i) => {
        const row = Math.floor(i / monthsPerRow);
        const col = i % monthsPerRow;

        const xOffset = margin.left + col * (monthWidth + 50);
        const yOffset = margin.top + row * (monthHeight + 50);

        const monthGroup = root.append('g').attr('transform', `translate(${xOffset}, ${yOffset})`);

        monthGroup
          .append('text')
          .attr('x', monthWidth / 2)
          .attr('y', -5)
          .attr('text-anchor', 'middle')
          .style('font-size', '16px')
          .text(d3.timeFormat('%B')(days[0]));

        monthGroup
          .selectAll('rect')
          .data(days)
          .join('rect')
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('x', (_, idx) => (idx % 7) * cellSize)
          .attr('y', (_, idx) => Math.floor(idx / 7) * cellSize)
          .attr('fill', (d) => {
            const key = d3.timeFormat('%Y-%m-%d')(d);
            return colorMap[dayStatusMap[key] || 'pending'];
          })
          .attr('stroke', '#fff');
      });
    }

    render();

    const onResize = () => {
      if (!cancelled) render();
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      cancelled = true;
    };
  }, [entries, year]);

  return <div ref={chartRef} />;
}

export default CalendarHeatmap;
